import React, { useMemo } from 'react'
import styles from './AllProjectsItemCard.module.scss'
import { CalendarDays, User, MapPin, HardHat, ExternalLink, MessageCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import defaultImage from '../../../assets/images/foto-bienvenida.jpg'
import { useActionGuard } from '../../../hooks/useActionGuard.ts'
import { openSendContactNotificationModal } from '../../../store/actions'
import { clearSubProjectBudgets } from '../../../store/actions/profileActions'

type ProjectImage = {
  image?: string | null
} | null

type SubProject = {
  name?: string | null
} | null

type ProjectUser = {
  id?: number | string | null
  type?: string | null
  razon_social?: string | null
  name?: string | null
  last_name?: string | null
  username?: string | null
} | null

type ProjectItem = {
  id: number | string
  name?: string | null
  startDate?: string | null
  endDate?: string | null
  zone?: string | null
  user?: ProjectUser
  description?: string | null
  image?: string | null
  proyecto_images?: ProjectImage[] | null
  subproyecto?: SubProject[] | null
}

interface AllProjectsItemCardProps {
  item: ProjectItem
  userData: { id?: number | string | null } | null | undefined
  clearSubProjectBudgets: () => void
  openSendContactNotificationModal: (user: unknown, extra?: { relatedPrivateProjectName?: string | null }) => void
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const looksLikeEmail = (value: string) => value.includes('@')

const AllProjectsItemCard: React.FC<AllProjectsItemCardProps> = ({
  item,
  userData,
  clearSubProjectBudgets,
  openSendContactNotificationModal: openContactModal,
}) => {
  const navigate = useNavigate()
  const { guard } = useActionGuard()

  const coverImage = useMemo(() => {
    if (Array.isArray(item?.proyecto_images) && item.proyecto_images.length > 0) {
      const imageSource = item.proyecto_images[0]?.image
      return imageSource || defaultImage
    }
    if (item?.image) {
      return item.image
    }
    return defaultImage
  }, [item])

  const ownerCard = useMemo(() => {
    const projectUser = item?.user
    if (!projectUser) {
      return { primary: 'Sin especificar', secondary: '' }
    }
    let name = ''
    if (projectUser.type === 'COMPANY') {
      name = (projectUser.razon_social || '').trim()
    } else {
      const firstName = projectUser.name || ''
      const lastName = projectUser.last_name || ''
      name = `${firstName} ${lastName}`.trim()
    }
    const usernameRaw = (projectUser.username || '').trim()
    const username = looksLikeEmail(usernameRaw) ? '' : usernameRaw
    const primary = name || username || 'Sin especificar'
    const secondary =
      name && username && username.toLowerCase() !== name.toLowerCase() ? username : ''
    return { primary, secondary }
  }, [item])

  const subprojects = useMemo(() => {
    if (!Array.isArray(item?.subproyecto)) {
      return [] as string[]
    }
    return item.subproyecto
      .map(subproject => (subproject?.name ? String(subproject.name) : null))
      .filter((name): name is string => Boolean(name))
  }, [item])

  const startDate = formatDate(item?.startDate)
  const endDate = formatDate(item?.endDate)
  const periodLabel =
    startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || 'Sin fecha definida'

  const publisherId = item?.user?.id
  const hasPublisherProfile =
    publisherId !== undefined && publisherId !== null && String(publisherId).length > 0

  const isOwnProject = useMemo(() => {
    const uid = userData?.id
    if (uid === undefined || uid === null) {
      return false
    }
    if (publisherId === undefined || publisherId === null) {
      return false
    }
    return String(uid) === String(publisherId)
  }, [userData?.id, publisherId])

  const primaryCtaLabel = isOwnProject ? 'Ir a mi proyecto' : 'Quiero cotizar'
  const primaryCtaAriaLabel = isOwnProject
    ? `Abrir tu proyecto ${item?.name || 'privado'}`
    : `Cotizar o ver detalle del proyecto ${item?.name || 'privado'}`

  const handleOpen = () => {
    clearSubProjectBudgets()
    navigate(`/obrasprivadas/${item.id}`)
  }

  const handleViewPublisherProfile = () => {
    guard('view_profile', () => {
      navigate(`/perfil/${String(publisherId)}`)
    })()
  }

  const handleContactPublisher = () => {
    if (!item.user) {
      return
    }
    guard('send_message', () => {
      openContactModal(item.user, { relatedPrivateProjectName: item.name ?? '' })
    })()
  }

  return (
    <article className={styles.card} style={{ '--card-cover': `url("${coverImage || '/placeholder.svg'}")` } as React.CSSProperties}>
      <div className={styles.lead}>
        <div className={styles.media}>
          <img className={styles.mediaImage} src={coverImage || '/placeholder.svg'} alt={`Imagen de ${item?.name || 'proyecto'}`} />
        </div>
        <div className={styles.leadCopy}>
          <h3 className={styles.title}>{item?.name}</h3>
          {item?.description && <p className={styles.description}>{item.description}</p>}
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.infoSection}>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <User size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Propietario</span>
                <span className={styles.infoValue}>{ownerCard.primary}</span>
                {ownerCard.secondary ? (
                  <span className={styles.infoValueSecondary}>{ownerCard.secondary}</span>
                ) : null}
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <MapPin size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Ubicación</span>
                <span className={styles.infoValue}>{item?.zone || 'Sin especificar'}</span>
              </div>
            </div>
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <CalendarDays size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Publicación del proyecto</span>
                <span className={styles.infoValue}>{periodLabel}</span>
                <span className={styles.infoValueSecondary}>Cotización: se define por rubro</span>
              </div>
            </div>
          </div>
          <div className={styles.rubrosBlock}>
            <div className={styles.rubrosHeader}>
              <div className={styles.infoIcon}>
                <HardHat size={16} />
              </div>
              <span className={styles.rubrosTitle}>Rubros a cotizar en el proyecto</span>
            </div>
            {subprojects.length > 0 ? (
              <div className={styles.tags}>
                {subprojects.map(subproject => (
                  <span key={subproject} className={styles.tag}>
                    {subproject}
                  </span>
                ))}
              </div>
            ) : (
              <p className={styles.rubrosEmpty}>Sin rubros asociados</p>
            )}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {hasPublisherProfile && !isOwnProject ? (
          <>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={handleViewPublisherProfile}
              aria-label={`Ver perfil del propietario de ${item?.name || 'proyecto'}`}
            >
              <User size={16} aria-hidden />
              <span>Ver perfil</span>
            </button>
            <button
              type="button"
              className={styles.ctaSecondary}
              onClick={handleContactPublisher}
              aria-label={`Contactar al propietario de ${item?.name || 'proyecto'}`}
            >
              <MessageCircle size={16} aria-hidden />
              <span>Contactar</span>
            </button>
          </>
        ) : null}
        <button
          className={styles.cta}
          onClick={guard('quote_project', handleOpen)}
          aria-label={primaryCtaAriaLabel}
        >
          <span>{primaryCtaLabel}</span>
          <ExternalLink size={16} />
        </button>
      </div>
    </article>
  )
}

const mapStateToProps = (state: { general?: { userData?: { id?: number | string | null } | null } }) => ({
  userData: state?.general?.userData ?? null,
})

export default connect(mapStateToProps, { clearSubProjectBudgets, openSendContactNotificationModal })(
  AllProjectsItemCard
)

